export const profile = {
  name: "Ahmed Zguir",
  role: "Data Scientist & Quant Finance Engineer",
  tagline:
    "Synthesizing stochastic calculus, machine learning, and algorithmic precision into systems that read markets like language.",
  email: "ahmedzguir64@gmail.com",
  location: "Lausanne, Switzerland",
  status: "Open to opportunities",
  cvUrl: "/cv.pdf",
  socials: {
    github: "https://github.com/zguir23",
    linkedin: "https://www.linkedin.com/in/ahmed-zguir-5b431b3a6",
  },
};

export const heroMetrics = [
  { value: "7+", label: "Projects shipped" },
  { value: "1.7B", label: "Param LLM aligned" },
  { value: "0.918", label: "F1 road segmentation" },
  { value: "5.41/6", label: "B.Sc. GPA at EPFL" },
];

export const marqueeItems = [
  "PYTHON",
  "DIVIDEND DERIVATIVES",
  "PYTORCH",
  "COMPUTER VISION",
  "STOCHASTIC CALCULUS",
  "CARRY & MOMENTUM",
  "FAMA-MACBETH",
  "HUGGING FACE",
  "MONTE CARLO",
  "PORTFOLIO OPTIMIZATION",
];

export const chapters = [
  {
    number: "01",
    title: "The Vision",
    body: "I merge empirical data science with quantitative finance — from pricing dividend derivatives to aligning language models, I build models that don't just describe the world, but anticipate it.",
  },
  {
    number: "02",
    title: "Mathematical Rigor",
    body: "Itô calculus, affine Laplace transforms, Fama-MacBeth regressions — I treat mathematics as an engineering material, and every model gets validated against real market data.",
  },
  {
    number: "03",
    title: "Engineering Excellence",
    body: "From LoRA adapters on A100 GPUs to production React apps used in active education research at the University of Toronto, I ship reproducible, documented work that survives out-of-sample — and real users.",
  },
];

export const skillCategories = [
  "All",
  "Data Science & AI",
  "Financial Engineering",
  "Software & Cloud",
  "Big Data Systems",
];

export const skills = [
  { name: "Python", years: 4, category: "Data Science & AI" },
  { name: "Pandas / NumPy", years: 3, category: "Data Science & AI" },
  { name: "PyTorch", years: 3, category: "Data Science & AI" },
  { name: "Computer Vision (CNNs)", years: 3, category: "Data Science & AI" },
  { name: "HuggingFace / Transformers", years: 3, category: "Data Science & AI" },
  { name: "LoRA · DPO · RLHF", years: 2, category: "Data Science & AI" },
  { name: "Scikit-learn", years: 3, category: "Data Science & AI" },
  { name: "Data Wrangling & Pipelines", years: 3, category: "Data Science & AI" },
  { name: "Web Scraping / REST APIs", years: 3, category: "Data Science & AI" },
  { name: "Unsupervised Learning · PCA", years: 3, category: "Data Science & AI" },
  { name: "GANs · LLMs · Self-Supervised", years: 2, category: "Data Science & AI" },
  { name: "Data Visualization", years: 3, category: "Data Science & AI" },
  { name: "Derivatives Pricing", years: 2, category: "Financial Engineering" },
  { name: "Stochastic Calculus", years: 3, category: "Financial Engineering" },
  { name: "Portfolio Optimization", years: 2, category: "Financial Engineering" },
  { name: "Econometrics / Fama-MacBeth", years: 2, category: "Financial Engineering" },
  { name: "Monte Carlo", years: 2, category: "Financial Engineering" },
  { name: "FX & Investments", years: 2, category: "Financial Engineering" },
  { name: "SQL / Databases", years: 3, category: "Software & Cloud" },
  { name: "React.js", years: 2, category: "Software & Cloud" },
  { name: "Java / C++ / Scala", years: 3, category: "Software & Cloud" },
  { name: "Git / LaTeX", years: 4, category: "Software & Cloud" },
  { name: "HPC / A100 GPUs", years: 2, category: "Software & Cloud" },
  { name: "Apache Spark", years: 2, category: "Big Data Systems" },
  { name: "Distributed Data Management", years: 2, category: "Big Data Systems" },
  { name: "Stream & Graph Processing", years: 2, category: "Big Data Systems" },
  { name: "Key-Value Stores / NoSQL", years: 2, category: "Big Data Systems" },
  { name: "Query Optimization & DB Tuning", years: 2, category: "Big Data Systems" },
  { name: "Transactions & Consistency Models", years: 2, category: "Big Data Systems" },
];

export const codeSnippet = `import numpy as np
from scipy.stats import norm

def black_scholes(S, K, T, r, sigma, kind="call"):
    d1 = (np.log(S / K) + (r + 0.5 * sigma**2) * T) \\
         / (sigma * np.sqrt(T))
    d2 = d1 - sigma * np.sqrt(T)
    if kind == "call":
        return S * norm.cdf(d1) - K * np.exp(-r * T) * norm.cdf(d2)
    return K * np.exp(-r * T) * norm.cdf(-d2) - S * norm.cdf(-d1)

# price = black_scholes(100, 105, 0.5, 0.03, 0.22)`;

const img = (id) =>
  `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop`;

const REPORTS = {
  dividends:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/itvfyylp_derivatives-2.pdf",
  qwen: "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/q6vy1kve_project_report.pdf",
  fx: "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/als1rhje_investment.pdf",
  blaine:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/m09ilpt3_BlaineCase.pdf",
  roadseg:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/krcz9iqw_report_2.pdf",
  cvd: "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/6b04ligx_853.pdf",
};

export const projects = [
  {
    id: "crm-dedup",
    title: "CRM Company Deduplication",
    subtitle: "Local LLM Entity Resolution",
    metric: "8h cache cycle",
    category: "AI Engineering",
    year: "2026",
    description:
      "A locally-hosted Qwen LLM (via Ollama) detects and merges duplicate company name variants in Omicron's CRM — e.g. “Société Générale” vs. “SocGen” — backed by an 8-hour refreshing cache.",
    longDescription:
      "Context — Pilotis is Omicron's internal recruitment & CRM platform, and every recruiter adds companies by hand. The problem — the same client ended up stored under many spellings (“Société Générale”, “SocGen”, “Societe Generale SA”), fragmenting pipelines and reporting. What I built — an entity-resolution service in Python/Flask on PostgreSQL that groups candidate variants, asks a locally-hosted Qwen3 LLM (via Ollama) to judge whether they refer to the same company, and merges confirmed duplicates. How it works — results live in an in-memory cache that refreshes on an 8-hour cycle, so lookups are instant while staying current as new records come in. Why local, not an API — the CRM holds confidential client data, so running the model on Omicron's own machines keeps everything in-house with no third-party LLM calls and no per-request cost.",
    tags: ["Python", "Flask", "Ollama", "Qwen3", "PostgreSQL"],
    image: "/projects/crm-dedup.webp",
    report: "",
    github: "",
    featured: false,
    span: "md:col-span-2",
  },
  {
    id: "dividend-derivative",
    title: "The Dividend Derivative",
    subtitle: "Pricing & Hedging Dividend Derivatives",
    metric: "1.49× futures hedge",
    category: "Financial Models",
    year: "2026",
    description:
      "Model-free and model-based pricing of dividend futures — a joint index–dividend affine model calibrated to market data, plus a replicating hedge for a 1Y ATM call.",
    longDescription:
      "Built at EPFL (Spring 2026). We priced dividend derivatives two ways: model-free via put-call parity and static replication, and model-based via a joint stochastic model for index and dividend dynamics solved with affine Laplace transforms. The model was calibrated to index level, dividend futures and an ATM call using Nelder-Mead optimization, achieving a near-exact fit. We then constructed a replicating portfolio for a one-year ATM call requiring ~1.49 two-year index futures contracts.",
    tags: ["Python", "Itô Calculus", "Affine Models", "Laplace Transform", "Nelder-Mead"],
    image: "/projects/dividend-derivative.webp",
    report: REPORTS.dividends,
    github: "",
    featured: true,
    span: "md:col-span-4",
  },
  {
    id: "qwen3-posttraining",
    title: "Qwen3-1.7B Post-Training",
    subtitle: "Verifiable Reasoning & Alignment",
    metric: "84% safety score",
    category: "Deep Learning",
    year: "2026",
    description:
      "Post-trained Qwen3-1.7B across math, safety, general knowledge and multilinguality with SFT + LoRA, DPO, GRPO, CoT distillation and model merging on A100 GPUs.",
    longDescription:
      "EPFL Spring 2026. We systematically studied post-training strategies for Qwen3-1.7B across four benchmarks. Key findings: strict boxed-format alignment is essential for scoring; clean supervised fine-tuning with curated data outperformed DPO, GRPO and thinking-mode refinements; and direct LoRA adapter merging boosted overall accuracy at the cost of some cross-domain interference. Best safety CI score: 84%.",
    tags: ["PyTorch", "HuggingFace", "LoRA", "DPO / GRPO", "A100 GPUs"],
    image: "/projects/qwen3-posttraining.webp",
    report: REPORTS.qwen,
    github: "https://github.com/zguir23/post-training-qwen3-reasoning",
    featured: false,
    span: "md:col-span-2",
  },
  {
    id: "road-segmentation",
    title: "Road Segmentation",
    subtitle: "Deep Learning on Aerial Imagery",
    metric: "0.918 F1 score",
    category: "Deep Learning",
    year: "2025",
    description:
      "Benchmarked U-Net++, DeepLabV3 and D-LinkNet for automatic road extraction from satellite imagery; multi-city data scaling and BCE-Dice loss pushed F1 to 0.918.",
    longDescription:
      "EPFL Machine Learning course (AICrowd challenge). We combined the EPFL AICrowd dataset with public Chicago and Boston–LA aerial datasets into a curated 4,537 image–mask training pool, applied geometric and photometric augmentation, and trained five encoder–decoder architectures with a weighted BCE + Dice loss. DeepLabV3 won with F1 0.918 / 0.956 pixel accuracy; a weighted majority-vote ensemble and morphological post-processing pushed generalization further across cities.",
    tags: ["PyTorch", "DeepLabV3", "U-Net++", "Computer Vision", "AICrowd"],
    image: "/projects/road-segmentation.webp",
    report: REPORTS.roadseg,
    github: "https://github.com/zguir23/road-segmentation",
    featured: false,
    span: "md:col-span-2",
  },
  {
    id: "fx-strategies",
    title: "FX Returns & Strategies",
    subtitle: "Currency Factor Investing",
    metric: "Carry: top Sharpe",
    category: "Financial Models",
    year: "2026",
    description:
      "Dissected carry, momentum and dollar factors across 6 G10 currencies (1991–2024); built in-sample and Fama-MacBeth out-of-sample mean-variance portfolios.",
    longDescription:
      "EPFL Spring 2026. Using WRDS Datastream spot/forward rates and FRED interest rates, we constructed cross-sectional and time-series carry, momentum and dollar strategies. Carry emerged as the most robust, statistically significant predictor of currency excess returns. Out-of-sample conditional mean-variance portfolios driven by Fama-MacBeth forecasts and EWMA covariance produced economically meaningful returns, while naive historical-mean forecasts proved too noisy.",
    tags: ["Python", "WRDS Datastream", "FRED", "Fama-MacBeth", "EWMA"],
    image: "/projects/fx-strategies.webp",
    report: REPORTS.fx,
    github: "https://github.com/zguir23/fx-returns-and-strategies",
    featured: false,
    span: "md:col-span-2",
  },
  {
    id: "cvd-prediction",
    title: "Heart Risk Prediction",
    subtitle: "CVD Classification on BRFSS Data",
    metric: "88.4% accuracy",
    category: "Machine Learning",
    year: "2025",
    description:
      "328k-patient CDC survey pipeline: systematic missingness surgery, one-hot + PCA design matrix, and tuned regularized logistic regression for coronary heart disease.",
    longDescription:
      "EPFL Machine Learning course (Project 1). On the BRFSS behavioral-risk survey (328,135 patients), we built a full preprocessing pipeline — targeted imputation of clinically meaningful variables, special-code cleaning, zero-variance and high-missingness filtering, QR-decomposition to drop linearly dependent one-hot columns, and PCA explaining 95% variance on continuous features. Tuned via 5-fold cross-validation, regularized logistic regression reached 88.4% accuracy with the best F1 under heavy class imbalance.",
    tags: ["Python", "Logistic Regression", "PCA", "Feature Engineering", "Cross-Validation"],
    image: "/projects/cvd-prediction.webp",
    report: REPORTS.cvd,
    github: "https://github.com/zguir23/heart-disease-prediction",
    featured: false,
    span: "md:col-span-2",
  },
  {
    id: "blaine-capital",
    title: "Blaine Kitchenware",
    subtitle: "Capital Structure Case Analysis",
    metric: "Repurchase evaluated",
    category: "Corporate Finance",
    year: "2025",
    description:
      "Harvard Business School case: analyzed a debt-financed share repurchase, capital structure trade-offs, dividend policy and value unlocked for the family owners.",
    longDescription:
      "Analyzed Blaine Kitchenware's unusually conservative, nearly debt-free balance sheet ($231M cash, ~22% EBITDA margin). Evaluated a proposed large share repurchase: impact on WACC, earnings per share, payout ratios, family ownership concentration and financial flexibility for future acquisitions. Benchmarked against peer capital structures to quantify the value a levered recapitalization could unlock.",
    tags: ["Valuation", "WACC", "Capital Structure", "Ratio Analysis", "Excel"],
    image: "/projects/blaine-capital.webp",
    report: REPORTS.blaine,
    github: "",
    featured: false,
    span: "md:col-span-4",
  },
];

export const projectFilters = [
  "All",
  "AI Engineering",
  "Financial Models",
  "Deep Learning",
  "Machine Learning",
  "Corporate Finance",
];

export const timeline = [
  {
    period: "2026",
    title: "AI Intern — Data Science & ML",
    org: "Omicron · Pilotis recruitment & CRM platform",
    type: "Experience",
    story:
      "My first time shipping an LLM feature into a product real people use every day — and learning that data quality is where AI earns its keep.",
    image: "/projects/crm-dedup.webp",
    points: [
      "Built an entity-resolution engine that detects and merges duplicate company variants in the CRM (“Société Générale” vs. “SocGen”)",
      "Ran Qwen3 locally via Ollama so client data never leaves the company's infrastructure — no third-party LLM APIs",
      "Designed an in-memory cache with an 8-hour refresh cycle to keep results current as new records arrive; shipped as a Flask service on PostgreSQL",
    ],
  },
  {
    period: "2025 — Present",
    title: "M.Sc. Data Science — Minor in Financial Engineering",
    org: "EPFL · École Polytechnique Fédérale de Lausanne",
    type: "Education",
    story:
      "The hardest math I've ever loved. EPFL keeps raising the bar — and every semester, it makes me sharper.",
    image:
      "https://thumb.wikimedia.org/wikipedia/commons/thumb/b/bf/EPFL_Rolex_Learning_Center_%40_EPFL_%40_Lausanne_%2836112479383%29.jpg/1280px-EPFL_Rolex_Learning_Center_%40_EPFL_%40_Lausanne_%2836112479383%29.jpg",
    points: [
      "Focus: machine learning, derivatives pricing, quantitative investing",
      "Team research across NLP/LLM alignment, dividend derivatives and FX portfolio construction",
    ],
  },
  {
    period: "2024 — 2025",
    title: "Software Engineering Researcher",
    org: "OISE · University of Toronto — Applied Psychology & Human Development",
    type: "Experience",
    story:
      "My first research role with real users — shipping features that teachers and K-12 students actually learn with.",
    image: null,
    points: [
      "Built Balance AI, a literacy & self-assessment platform for K-12 personalized learning",
      "Designed a gamification module boosting student engagement and retention",
      "Hardened security and load stability; shipped React.js front-end used in active research",
    ],
  },
  {
    period: "2024 — 2025",
    title: "Exchange Year",
    org: "University of Toronto",
    type: "Education",
    story:
      "An enriching year abroad — new people, new cultures, new ways of thinking. Toronto taught me as much outside the classroom as inside it.",
    image:
      "https://images.unsplash.com/photo-1517090504586-fde19ea6066f?crop=entropy&cs=srgb&fm=jpg&q=85&w=900&auto=format&fit=crop",
    points: [
      "Graduate-level coursework in machine learning and software engineering",
      "Cross-cultural research collaboration in North America",
    ],
  },
  {
    period: "2022 — 2025",
    title: "B.Sc. Communication Systems Engineering — GPA 5.41/6",
    org: "EPFL · École Polytechnique Fédérale de Lausanne",
    type: "Education",
    story:
      "Brutal at times, formative always — this degree built the engineering backbone everything else stands on.",
    image:
      "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/zsx07b2m_IMG_1017.webp",
    points: [
      "Foundations: signal processing, statistics, random processes, networks, systems",
      "Programming across Python, Java, C, C++, Scala, Julia",
    ],
  },
];

export const coursework = [
  {
    degree: "M.Sc. Data Science · Minor Financial Engineering",
    school: "EPFL",
    period: "2025 — Present",
    groups: [
      {
        label: "Data & AI",
        courses: [
          "Machine Learning",
          "Modern Natural Language Processing",
          "Applied Data Analysis",
          "Systems for Data Management & Data Science",
          "Financial Big Data",
        ],
      },
      {
        label: "Quant Finance",
        courses: [
          "Derivatives",
          "Investments",
          "Quantitative Financial Risk Management",
          "Principles of Finance",
          "Probability & Stochastic Calculus",
          "Algebra",
        ],
      },
    ],
  },
  {
    degree: "B.Sc. Communication Systems Engineering",
    school: "EPFL",
    period: "2022 — 2025",
    groups: [
      {
        label: "Computing",
        courses: [
          "Algorithms I",
          "Artificial Intelligence",
          "Introduction to Machine Learning",
          "Computer Security",
          "Computer Systems",
          "Software Construction",
          "Practice of Object-Oriented Programming",
          "Introduction to Programming",
          "Digital System Design",
        ],
      },
      {
        label: "Math & Signals",
        courses: [
          "Analysis I – IV",
          "Linear Algebra",
          "Probability & Statistics",
          "Random Processes",
          "Signal Processing",
          "Digital Communications",
          "Advanced Information, Computation, Communication I & II",
          "General Physics: Mechanics & Electromagnetism",
        ],
      },
    ],
  },
];

export const contactInterests = [
  "Full-time roles",
  "Freelance / Consulting",
  "Research collaboration",
  "Speaking & mentoring",
];

export const aboutBio =
  "Data Science M.Sc. student at EPFL with a minor in Financial Engineering and a B.Sc. in Communication Systems. I've lived and studied in Switzerland and Canada, working where AI meets financial markets — from post-training LLMs to pricing dividend derivatives. I'm drawn to quantitative finance and algorithmic trading, I contribute to open-source ML/NLP, and off the keyboard I chase good light with my camera. Arabic native, fluent in French and English, conversational Spanish.";

export const aboutPhotos = [
  {
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/wxj9wq0n_IMG_1054%202.webp",
    alt: "Ahmed Zguir — EPFL B.Sc. graduation",
  },
  {
    src: "https://customer-assets-eiarnc6j.emergentagent.net/job_work-gallery-988/artifacts/zsx07b2m_IMG_1017.webp",
    alt: "Ahmed Zguir at the EPFL graduation ceremony",
  },
];

const FIG = "/figures";

export const beyondItems = [
  {
    icon: "photography",
    title: "Photography",
    text: "Chasing light from Lake Geneva to the Amalfi Coast — framing the world between datasets.",
    photo: "/photos/lake-geneva.webp",
  },
  {
    icon: "travel",
    title: "Traveling",
    text: "New cities, new cultures, new perspectives. Every trip rewires how I see problems.",
    photo: "/photos/giza-sunset.webp",
  },
];

export const beyondGallery = [
  { src: "/photos/giza-sunset.webp", caption: "Giza, Egypt — golden hour" },
  { src: "/photos/bamboo-nest.webp", caption: "Thailand — jungle nest viewpoint" },
  { src: "/photos/lake-geneva.webp", caption: "Lake Geneva, Switzerland" },
  { src: "/photos/sea-alley.webp", caption: "Italy — Mediterranean blues" },
  { src: "/photos/amalfi-bridge.webp", caption: "Amalfi Coast, Italy" },
];

export const services = [
  {
    id: "ml-dl",
    num: "01",
    title: "MACHINE LEARNING & DEEP LEARNING",
    desc: "From LoRA adapters to full fine-tuning pipelines on A100 clusters — I build, align and evaluate models that hold up out-of-sample. Post-trained Qwen3-1.7B to an 84% safety score and hit 0.918 F1 on aerial road segmentation.",
    tags: ["PyTorch", "Transformers", "LoRA / DPO / GRPO", "Computer Vision", "A100 HPC"],
    image: "/services/ml-dl.webp",
    caption: "neural networks, aligned",
  },
  {
    id: "quant",
    num: "02",
    title: "QUANTITATIVE FINANCE",
    desc: "Derivatives pricing, stochastic models and factor investing on real market data. Priced dividend futures with a calibrated affine index–dividend model, and dissected carry & momentum premia across 30 years of G10 FX.",
    tags: ["Derivatives Pricing", "Stochastic Calculus", "Factor Investing", "Monte Carlo"],
    image: "/services/quant.webp",
    caption: "vol surfaces & carry",
  },
  {
    id: "data",
    num: "03",
    title: "DATA SCIENCE & ANALYTICS",
    desc: "End-to-end pipelines on messy real-world data: imputation strategies, feature engineering, PCA/QR design matrices and cross-validated models — like predicting heart-disease risk across 328k CDC survey responses.",
    tags: ["EDA", "Feature Engineering", "Econometrics", "Forecasting"],
    image: "/services/data.webp",
    caption: "data in motion",
  },
  {
    id: "fullstack",
    num: "04",
    title: "FULL-STACK AI PRODUCTS",
    desc: "Research code is only half the job — I ship it. React + FastAPI apps, streaming LLM interfaces, and research platforms used by real people, like the Balance AI literacy tool at the University of Toronto.",
    tags: ["React", "FastAPI", "Streaming UX", "Docker", "SQL"],
    image: "/services/fullstack.webp",
    caption: "ship it, don't shelve it",
  },
];
